import {
  getCompetitorControllerGetCompetitorListQueryKey,
  getCompetitorControllerGetCompetitorRelationQueryKey,
  useCompetitorControllerMark,
} from "@/api/types/main";
import type {
  CompetitorRelationDetailResponse,
  CompetitorRelationListResponse,
} from "@/api/types/main/schemas";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Star } from "lucide-react";
import { useState, type MouseEvent } from "react";
import { toast } from "sonner";

interface CompetitorMarkedProps {
  isMarked: boolean;
  sku: number;
  competitorSku: number;
  date: string;
  size?: number;
}

type MarkMutationContext = {
  previousList?: CompetitorRelationListResponse[];
  previousDetail?: CompetitorRelationDetailResponse;
};

function getApiErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error ? error.message : undefined;
  }

  const data = error.response?.data as { message?: unknown } | undefined;

  if (typeof data?.message === "string" && data.message.length > 0) {
    return data.message;
  }

  return error.message;
}

export function CompetitorMarked({
  isMarked,
  sku,
  competitorSku,
  date,
  size = 20,
}: CompetitorMarkedProps) {
  const queryClient = useQueryClient();

  const [confirmOpen, setConfirmOpen] = useState(false);

  const dateParam = { date };

  const listQueryKey = getCompetitorControllerGetCompetitorListQueryKey(sku, dateParam);

  const detailQueryKey = getCompetitorControllerGetCompetitorRelationQueryKey(
    sku,
    competitorSku,
    dateParam
  );

  const { mutate, isPending } = useCompetitorControllerMark({
    mutation: {
      onMutate: async (variables) => {
        const nextMarked = variables.data.isMarked;

        await Promise.all([
          queryClient.cancelQueries({
            queryKey: listQueryKey,
            exact: true,
          }),
          queryClient.cancelQueries({
            queryKey: detailQueryKey,
            exact: true,
          }),
        ]);

        const previousList =
          queryClient.getQueryData<CompetitorRelationListResponse[]>(listQueryKey);

        const previousDetail =
          queryClient.getQueryData<CompetitorRelationDetailResponse>(detailQueryKey);

        queryClient.setQueryData<CompetitorRelationListResponse[]>(listQueryKey, (old) => {
          if (!old) return old;

          return nextMarked
            ? old.map((row) => (row.sku === competitorSku ? { ...row, isMarked: true } : row))
            : old.filter((row) => row.sku !== competitorSku);
        });

        queryClient.setQueryData<CompetitorRelationDetailResponse>(detailQueryKey, (old) =>
          old ? { ...old, isMarked: nextMarked } : old
        );

        return {
          previousList,
          previousDetail,
        } satisfies MarkMutationContext;
      },

      onError: (error, _variables, context) => {
        if (context?.previousList !== undefined) {
          queryClient.setQueryData(listQueryKey, context.previousList);
        }

        if (context?.previousDetail !== undefined) {
          queryClient.setQueryData(detailQueryKey, context.previousDetail);
        }

        const apiMessage = getApiErrorMessage(error);

        toast.error(
          apiMessage ? `Не удалось изменить отметку. ${apiMessage}` : "Не удалось изменить отметку"
        );
      },

      onSuccess: (_data, variables) => {
        toast.success(
          variables.data.isMarked ? "Конкурент отмечен" : "Конкурент удалён из отмеченных"
        );
      },

      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: listQueryKey,
          exact: true,
        });

        queryClient.invalidateQueries({
          queryKey: detailQueryKey,
          exact: true,
        });
      },
    },
  });

  const updateMarked = (nextMarked: boolean) => {
    mutate({
      sku,
      data: {
        isMarked: nextMarked,
        date,
      },
      params: {
        competitor: competitorSku,
      },
    });
  };

  const handleStarClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isPending) return;

    if (isMarked) {
      setConfirmOpen(true);
      return;
    }

    updateMarked(true);
  };

  const handleConfirmRemove = () => {
    setConfirmOpen(false);
    updateMarked(false);
  };

  return (
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={handleStarClick}
        disabled={isPending}
        aria-pressed={isMarked}
        aria-label={
          isMarked ? "Удалить конкурента из отмеченных" : "Добавить конкурента в отмеченные"
        }
        title={isMarked ? "Удалить из отмеченных" : "Добавить в отмеченные"}
        className="h-9 w-9"
      >
        <Star
          size={size}
          className={cn(
            "transition-colors",
            isMarked ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
          )}
        />
      </Button>

      <AlertDialogContent onClick={(event) => event.stopPropagation()} className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить из отмеченных?</AlertDialogTitle>

          <AlertDialogDescription>
            Конкурент будет удалён из списка отмеченных. Это действие невозможно отменить, удаление
            отметки так же скроет выбранную карточку из таблицы.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Отмена</AlertDialogCancel>

          <AlertDialogAction
            disabled={isPending}
            onClick={handleConfirmRemove}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/80"
          >
            {isPending ? "Удаляем..." : "Удалить"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
